import type { UiNodeInputAttributes } from '@ory/client';
import type { UiNode } from '@ory/client';
import { useState } from 'react';
import ResponseMessage from '@/components/Error/ResponseMessage';
import { Spacer, VStack } from '@/components/Layout';
import { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot } from '@/components/ui/input-otp';
import { useNodeTranslation } from '@/features/auth/hooks/useNodeTranslation';

/** 表示順序: 入力フィールドは中間に配置 */
export const ORDER = 50;
const OTP_LENGTH = 6;
type Props = {
  node: UiNode;
  disabled?: boolean;
};

/**
 * ワンタイムパスワード（6桁）入力
 *
 * 既存の `InputOTP`（`components/ui/input-otp.tsx`）を利用する。
 * 送信値は hidden input に詰めて Ory に送る。
 */
export function OneTimePassword({
  node,
  disabled
}: Props) {
  const {
    translateUiText
  } = useNodeTranslation();
  const attrs = node.attributes as UiNodeInputAttributes;
  const name = attrs.name;
  const label = node.meta?.label ? translateUiText(node.meta.label) : undefined;
  const errorMessages = node.messages?.filter(msg => msg.type === 'error').map(msg => translateUiText(msg));
  const hasError = Boolean(errorMessages && errorMessages.length > 0);
  const normalize = (raw: string) => raw.replaceAll(/\D/g, '').slice(0, OTP_LENGTH);
  const [value, setValue] = useState(() => normalize(typeof attrs.value === 'string' ? attrs.value : ''));
  return <VStack className="gap-1.5">
      {label && <label className="text-xs font-semibold text-neutral-muted-on-fill">
          {label}
        </label>}

      <input type="hidden" name={name} value={value} />

      <InputOTP value={value} onChange={next => setValue(normalize(next))} maxLength={OTP_LENGTH} disabled={disabled} aria-invalid={hasError || undefined} aria-label={label} containerClassName="w-full">
        <InputOTPGroup className="gap-2">
          <InputOTPSlot index={0} className="w-8 h-9 rounded-md border border-neutral-muted-border" />
          <InputOTPSlot index={1} className="w-8 h-9 rounded-md border border-neutral-muted-border" />
          <InputOTPSlot index={2} className="w-8 h-9 rounded-md border border-neutral-muted-border" />
        </InputOTPGroup>
        <InputOTPSeparator />
        <InputOTPGroup className="gap-2">
          <InputOTPSlot index={3} className="w-8 h-9 rounded-md border border-neutral-muted-border" />
          <InputOTPSlot index={4} className="w-8 h-9 rounded-md border border-neutral-muted-border" />
          <InputOTPSlot index={5} className="w-8 h-9 rounded-md border border-neutral-muted-border" />
        </InputOTPGroup>
      </InputOTP>

      {hasError && <>
          <Spacer variant="xxs" />
          <ResponseMessage type="error" size="xs">
            {errorMessages?.[0]}
          </ResponseMessage>
        </>}
    </VStack>;
}